/**
 * Mobile shell — Encapsulates the entire mobile UI (topbar, drawer sidebar,
 * content area, bottom navigation bar). Pure render component driven by
 * shell props passed from the parent Layout orchestrator.
 *
 * Goal: split the 1000+ lines Layout.js into smaller, maintainable shells.
 */
import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  List, X, ArrowLeft, CaretRight, Bell, Gear, SignOut, ArrowsClockwise,
} from '@phosphor-icons/react';
import { NotificationPanel } from '../layout/LayoutParts';

const MobileShell = ({
  children,
  envBadge,
  environment,
  // top bar
  canGoBack,
  handleBack,
  mobileMenuOpen,
  setMobileMenuOpen,
  settings,
  notifRef,
  notifications,
  unreadCount,
  showNotifPanel,
  setShowNotifPanel,
  fetchNotifications,
  markAllRead,
  handleNotifClick,
  t,
  user,
  // links
  links,
  navBadges,
  gmailUnread,
  markNavSeen,
  expandedGroups,
  toggleGroup,
  // bottom nav
  bottomNavLinks,
  location,
  // actions
  navigate,
  handleLogout,
}) => {
  return (
    <>
      {envBadge}
      <div className="app-layout-mobile" style={{ paddingTop: environment !== 'production' ? '24px' : 0 }}>
        {/* Mobile top bar */}
        <header className="mobile-topbar" data-testid="mobile-topbar">
          {canGoBack ? (
            <button
              data-testid="mobile-back-btn"
              onClick={handleBack}
              className="mobile-hamburger-btn"
              aria-label="Retour"
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <ArrowLeft size={24} weight="bold" />
            </button>
          ) : (
            <button
              data-testid="mobile-hamburger"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="mobile-hamburger-btn"
              aria-label="Menu"
            >
              <List size={26} weight="bold" />
            </button>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {settings.logo_url ? (
              <img src={settings.logo_url} alt="Logo" style={{ height: '28px', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
              <img src="/batiboys-logo.svg" alt="BATI-BOYS" style={{ height: '28px', objectFit: 'contain' }} />
            )}
            <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '1.1rem', color: settings.primary_color }}>
              {settings.company_name}
            </span>
          </div>

          <div ref={notifRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 2 }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              title="Rafraichir la page"
              data-testid="refresh-page-btn"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', display: 'flex', color: '#5E5F66' }}
            >
              <ArrowsClockwise size={22} />
            </button>
            <button
              data-testid="notification-bell"
              onClick={() => { setShowNotifPanel(!showNotifPanel); if (!showNotifPanel) fetchNotifications(); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '6px', display: 'flex' }}
            >
              <Bell size={24} weight={unreadCount > 0 ? 'fill' : 'regular'} style={{ color: unreadCount > 0 ? settings.primary_color : '#5E5F66' }} />
              {unreadCount > 0 && (
                <span data-testid="notification-badge" style={{
                  position: 'absolute', top: '0', right: '0', backgroundColor: '#C62828', color: 'white',
                  borderRadius: '50%', width: '16px', height: '16px', fontSize: '0.6rem', fontWeight: '700',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </button>

            {showNotifPanel && (
              <NotificationPanel
                notifications={notifications}
                unreadCount={unreadCount}
                primaryColor={settings.primary_color}
                t={t}
                onMarkAllRead={markAllRead}
                onClose={() => setShowNotifPanel(false)}
                onNotifClick={handleNotifClick}
                style={{ position: 'fixed', top: '56px', left: '8px', right: '8px', maxHeight: '70vh' }}
              />
            )}
          </div>
        </header>

        {/* Mobile overlay sidebar */}
        {mobileMenuOpen && (
          <div className="mobile-overlay" data-testid="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
        )}
        <aside className={`mobile-sidebar ${mobileMenuOpen ? 'open' : ''}`} data-testid="mobile-sidebar">
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border, #E2E2E6)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="user-info" style={{ marginBottom: 0 }}>
              <div className="user-avatar" style={{ backgroundColor: settings.primary_color, width: '36px', height: '36px', fontSize: '0.85rem' }}>
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="user-details">
                <div className="user-name">{user?.name || 'Utilisateur'}</div>
                <div className="user-role">{user?.role === 'admin' ? t('role.admin') : t('role.employee')}</div>
              </div>
            </div>
            <button onClick={() => setMobileMenuOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
              <X size={24} color="#5E5F66" />
            </button>
          </div>

          <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
            {links.map((link) => {
              const badge = link.to === '/gmail' ? gmailUnread : (navBadges[link.to] || 0);
              const hasChildren = Array.isArray(link.children) && link.children.length > 0;
              const isExpanded = !!expandedGroups[link.to];
              return (
                <React.Fragment key={link.to}>
                  <NavLink
                    to={link.to}
                    className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
                    end={link.to === '/'}
                    onClick={(e) => {
                      if (hasChildren) {
                        e.preventDefault();
                        toggleGroup(link.to);
                      } else {
                        if (badge > 0) markNavSeen(link.to);
                        setMobileMenuOpen(false);
                      }
                    }}
                    style={({ isActive }) => ({ color: isActive ? settings.primary_color : undefined })}
                  >
                    <link.icon size={22} weight="regular" />
                    <span style={{ flex: 1 }}>{link.label}</span>
                    {badge > 0 && (
                      <span
                        data-testid={link.to === '/gmail' ? 'gmail-unread-badge-mobile' : undefined}
                        style={{ minWidth: '18px', height: '18px', borderRadius: '9px', backgroundColor: '#C62828', color: 'white', fontSize: '0.7rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px', lineHeight: 1 }}>
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )}
                    {hasChildren && (
                      <span style={{ marginLeft: '4px', display: 'inline-flex', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', opacity: 0.6 }}>
                        <CaretRight size={14} weight="bold" />
                      </span>
                    )}
                  </NavLink>
                  {hasChildren && isExpanded && (
                    <div className="nav-subitems">
                      {link.children.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          className="nav-subitem"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </nav>

          <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border, #E2E2E6)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button
              data-testid="mobile-settings-btn"
              className="btn btn-secondary"
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
              onClick={() => { setMobileMenuOpen(false); navigate('/settings'); }}
            >
              <Gear size={20} weight="regular" />
              <span>Paramètres</span>
            </button>
            <button data-testid="logout-btn" className="btn btn-secondary" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }} onClick={handleLogout}>
              <SignOut size={20} weight="bold" />
              <span>{t('nav.logout')}</span>
            </button>
          </div>
        </aside>

        {/* Mobile content */}
        <main className={`mobile-content ${user?.role === 'employee' ? 'no-bottom-nav' : ''}`} data-testid="mobile-main-content">
          {children}
        </main>

        {/* Bottom navigation bar — hidden for employees (replaced by home screen tiles) */}
        {user?.role !== 'employee' && (
          <nav className="mobile-bottom-nav" data-testid="mobile-bottom-nav">
            {bottomNavLinks.map((link) => {
              const isActive = location.pathname === link.to || (link.to !== '/' && location.pathname.startsWith(link.to));
              return (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={`mobile-bottom-item ${isActive ? 'active' : ''}`}
                  end={link.to === '/'}
                >
                  <link.icon size={22} weight={isActive ? 'fill' : 'regular'} />
                  <span>{link.label}</span>
                </NavLink>
              );
            })}
            <button
              className={`mobile-bottom-item ${mobileMenuOpen ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-bottom-more"
            >
              <List size={22} weight={mobileMenuOpen ? 'fill' : 'regular'} />
              <span>Plus</span>
            </button>
          </nav>
        )}
      </div>
    </>
  );
};

export default MobileShell;
